import React, { useEffect, useState } from "react";
import { C } from "../lib/colors.js";
import { ui } from "../lib/telemetry/instrument.js";
import { RailPanelHeading } from "./NavRail.jsx";
import { Button, IconButton } from "./ui/Primitives.jsx";
import { IconTrash } from "./ui/Icons.jsx";

/**
 * The chat panel's header: the rail heading plus a "Clear chat history" control that confirms
 * inline before wiping this browser's chat journal. Without the rail there is no heading, so
 * the control sits in a slim row of its own.
 *
 * @param {{label: string, showHeading?: boolean, canClear: boolean, onClear: () => void}} props
 */
export default function ChatPanelHeader({ label, showHeading = true, canClear, onClear }) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- nothing left to clear
    if (!canClear) setConfirming(false);
  }, [canClear]);
  // Escape keeps the history, from either button (focus opens on Keep).
  const keepOnEscape = (e) => {
    if (e.key === "Escape") setConfirming(false);
  };
  const clearButton = canClear && !confirming && (
    <IconButton
      {...ui("chat.clear-history")}
      label="Clear chat history"
      onClick={() => setConfirming(true)}
      style={
        showHeading
          ? { position: "absolute", top: 9, right: 12, width: 28, height: 28 }
          : { width: 28, height: 28 }
      }
    >
      <IconTrash style={{ width: 16, height: 16 }} />
    </IconButton>
  );
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      {showHeading && <RailPanelHeading label={label} />}
      {showHeading
        ? clearButton
        : clearButton && (
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 8px 0" }}>
              {clearButton}
            </div>
          )}
      {confirming && (
        <div
          role="group"
          aria-label="Clear chat history"
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            padding: "8px 12px 8px 16px",
            borderBottom: `1px solid ${C.border}`,
            background: C.rowInactive,
            color: C.grayL,
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          <span style={{ flex: "1 1 160px", minWidth: 0 }}>
            Clear all chat history in this browser?
          </span>
          <span style={{ display: "inline-flex", gap: 4 }}>
            <Button
              {...ui("chat.clear-history-confirm")}
              size="sm"
              variant="danger"
              onKeyDown={keepOnEscape}
              onClick={() => {
                setConfirming(false);
                onClear?.();
              }}
            >
              Clear
            </Button>
            <Button
              {...ui("chat.clear-history-keep")}
              size="sm"
              variant="ghost"
              autoFocus
              onKeyDown={keepOnEscape}
              onClick={() => setConfirming(false)}
            >
              Keep
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
